import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../../services/supabase'
import { useSimpleHqAccess } from './useSimpleHqAccess'
import { todayIso } from './hqUtils'

type LeaveRow = {
  id: string
  user_id: string
  start_date: string
  end_date: string
  status: string
  leave_type_id: string
}

type RosterRow = { user_id: string; email: string }

export function LeaveCalendarView() {
  const { companyId } = useSimpleHqAccess()
  const [anchor, setAnchor] = useState(() => {
    const d = new Date()
    return new Date(d.getFullYear(), d.getMonth(), 1)
  })
  const [rows, setRows] = useState<LeaveRow[]>([])
  const [types, setTypes] = useState<Record<string, string>>({})
  const [roster, setRoster] = useState<RosterRow[]>([])
  const [statusFilter, setStatusFilter] = useState<'all' | 'approved' | 'pending'>('all')

  const monthStart = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-01`
  const monthEndDate = new Date(anchor.getFullYear(), anchor.getMonth() + 1, 0)
  const monthEnd = `${monthEndDate.getFullYear()}-${String(monthEndDate.getMonth() + 1).padStart(2, '0')}-${String(monthEndDate.getDate()).padStart(2, '0')}`

  useEffect(() => {
    ;(async () => {
      const [{ data: leaves }, { data: t }, { data: r }] = await Promise.all([
        supabase
          .from('leave_requests')
          .select('id, user_id, start_date, end_date, status, leave_type_id')
          .eq('company_id', companyId)
          .in('status', ['pending', 'approved'])
          .lte('start_date', monthEnd)
          .gte('end_date', monthStart),
        supabase.from('leave_types').select('id, name').eq('company_id', companyId),
        supabase.rpc('simple_hq_roster'),
      ])
      setRows((leaves as LeaveRow[]) || [])
      const tm: Record<string, string> = {}
      for (const x of t || []) tm[x.id] = x.name
      setTypes(tm)
      setRoster((r as RosterRow[]) || [])
    })()
  }, [companyId, monthStart, monthEnd])

  const emailById = useMemo(() => {
    const m: Record<string, string> = {}
    for (const r of roster) m[r.user_id] = r.email
    return m
  }, [roster])

  const daysInMonth = monthEndDate.getDate()
  const startDow = new Date(anchor.getFullYear(), anchor.getMonth(), 1).getDay()
  const cells: (number | null)[] = []
  for (let i = 0; i < startDow; i++) cells.push(null)
  for (let d = 1; d <= daysInMonth; d++) cells.push(d)

  const filtered = rows.filter((r) => statusFilter === 'all' || r.status === statusFilter)

  const forDay = (day: number) => {
    const iso = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    return filtered.filter((r) => r.start_date <= iso && r.end_date >= iso)
  }

  const label = anchor.toLocaleString(undefined, { month: 'long', year: 'numeric' })

  return (
    <div>
      <p className="hq-eyebrow">Leave</p>
      <h1 className="hq-title">Team calendar</h1>
      <p className="hq-sub">
        Approved is green/solid; pending is amber/dashed with a Pending label. Today is {todayIso()}.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          className="hq-btn"
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() - 1, 1))}
        >
          Prev
        </button>
        <strong>{label}</strong>
        <button
          type="button"
          className="hq-btn"
          onClick={() => setAnchor(new Date(anchor.getFullYear(), anchor.getMonth() + 1, 1))}
        >
          Next
        </button>
        <select
          className="ml-auto"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}
          aria-label="Status filter"
        >
          <option value="all">All</option>
          <option value="approved">Approved</option>
          <option value="pending">Pending</option>
        </select>
      </div>

      <div className="grid grid-cols-7 gap-2 text-xs text-[var(--hq-muted)] mb-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
          <div key={d} className="px-1">
            {d}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-2">
        {cells.map((day, idx) =>
          day == null ? (
            <div key={`e-${idx}`} />
          ) : (
            <div key={day} className="hq-cal-day">
              <strong>{day}</strong>
              {forDay(day).map((r) => {
                const who = (emailById[r.user_id] || '?').split('@')[0]
                const typeName = types[r.leave_type_id] || 'Leave'
                const isPending = r.status !== 'approved'
                return (
                  <span
                    key={r.id}
                    className={`hq-cal-chip ${isPending ? 'pending' : 'approved'}`}
                    title={`${isPending ? 'Pending' : 'Approved'} · ${emailById[r.user_id] || ''} · ${typeName}`}
                  >
                    {isPending ? `Pending · ${who} · ${typeName}` : `${who} · ${typeName}`}
                  </span>
                )
              })}
            </div>
          ),
        )}
      </div>
    </div>
  )
}
